/**
 * F39 (Wave 2C) — admin featured-placements API client.
 *
 * Endpoints (plan §9):
 *   GET    /admin/placement-inventory                       → listInventory
 *   PUT    /admin/placement-inventory/:type                 → editInventory (If-Match + reason)
 *   GET    /admin/placements                                → listPlacements (filters status/type/venue)
 *   GET    /admin/placements/approvals                      → listApprovals (FIFO pending_approval queue)
 *   GET    /admin/placements/:id                            → placementDetail (creative + audit)
 *   POST   /admin/placements/:id/approve                    → approvePlacement
 *   POST   /admin/placements/:id/reject                     → rejectPlacement (mandatory reason)
 *   POST   /admin/placements/:id/cancel                     → cancelPlacement  (mandatory reason + optional credit grant)
 *   POST   /admin/placements/promote-tournament             → promoteTournament (free admin platform-promote, F41)
 *   POST   /admin/venues/:venueId/placement-credits/grant   → grantCredit      (mandatory reason)
 *   GET    /admin/placements/analytics                      → analytics        (platform performance)
 *
 * Permission gating happens server-side (`placement_inventory:read/write`,
 * `placement_approvals:review/approve`, `placement_credits:grant`). The
 * page-level <ProtectedRoute> gates which routes a given admin role can
 * navigate to; the API client forwards requests and relies on the server to
 * enforce RBAC.
 *
 * Component tests do NOT exercise this real client directly — vitest's config
 * alias rewrites `./api/placements` → `./api/placements.fixture` (the
 * F23/F35/F38 admin convention).
 */
import type {
  ApprovePlacementInput,
  CancelPlacementInput,
  EditPlacementInventoryInput,
  GrantPlacementCreditInput,
  Placement,
  PlacementCreditAdjustment,
  PlacementInventoryConfig,
  PlacementPerformanceSummary,
  PlacementStatus,
  PlacementType,
  PromoteTournamentPlacementInput,
  RejectPlacementInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

// Re-export the shared ApiError so callers/tests can `instanceof`-check the
// same class the rest of the admin app throws.
export { ApiError as AdminPlacementError } from '../auth/types';

// ─── Read-side view types ────────────────────────────────────────────────────

/** One row of the admin cross-venue placements list / approval queue. */
export interface AdminPlacementRow extends Placement {
  /** Resolved venue display name (server-side join). */
  readonly venueName: string | null;
  readonly venueSlug: string | null;
  /** Per-placement performance tally (server-side aggregate). */
  readonly performance?: PlacementPerformanceSummary | null;
}

export interface AdminPlacementsListResponse {
  readonly data: readonly AdminPlacementRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/** The placement detail (row + creative + per-placement performance series). */
export interface AdminPlacementDetailResponse extends AdminPlacementRow {
  /** Approving / rejecting / cancelling admin display names (server-side join). */
  readonly approvedByAdminDisplayName: string | null;
  readonly cancelledByAdminDisplayName: string | null;
  /**
   * Resolved public CDN URL for the creative banner image (server-side join
   * from `creativeImageS3Key`). Null when the placement has no banner image
   * (e.g. a sponsored_notification carries only headline + copy).
   */
  readonly creativeImageUrl: string | null;
}

export interface AdminInventoryListResponse {
  readonly data: readonly PlacementInventoryConfig[];
}

/** Platform-wide performance analytics (plan §9). */
export interface AdminPlacementAnalyticsResponse {
  /** Revenue (net out-of-pocket, USD minor units) grouped by placement type. */
  readonly revenueByType: ReadonlyArray<{
    readonly placementType: PlacementType;
    readonly revenueMinor: number;
  }>;
  /** Fill rate (sold / capacity, 0..1) grouped by scope kind. */
  readonly fillRateByScope: ReadonlyArray<{
    readonly scopeKind: string;
    readonly fillRate: number;
    readonly soldSlots: number;
    readonly capacitySlots: number;
  }>;
  /** Platform-wide funnel totals. */
  readonly totals: {
    readonly impressions: number;
    readonly clicks: number;
    readonly conversions: number;
    readonly ctr: number;
    readonly cpcMinor: number;
    readonly cpaMinor: number;
    readonly revenueMinor: number;
    readonly currency: string;
  };
  /** Top venues by placement spend (net out-of-pocket). */
  readonly topVenues: ReadonlyArray<{
    readonly venueId: string;
    readonly venueName: string | null;
    readonly spendMinor: number;
    readonly placements: number;
  }>;
}

// ─── Query option types ───────────────────────────────────────────────────────

export interface ListPlacementsOpts {
  /** Single status value OR comma-separated list; omitted / 'all' = no filter. */
  readonly status?: PlacementStatus | readonly PlacementStatus[] | 'all';
  readonly type?: PlacementType | 'all';
  readonly venueId?: string;
  readonly page?: number;
  readonly perPage?: number;
}

export interface ListApprovalsOpts {
  readonly page?: number;
  readonly perPage?: number;
}

function buildPlacementsQuery(opts: ListPlacementsOpts): string {
  const params: Record<string, string | number | boolean | undefined | null> = {
    venueId: opts.venueId,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') {
    params.status = Array.isArray(opts.status) ? opts.status.join(',') : (opts.status as string);
  }
  if (opts.type && opts.type !== 'all') {
    params.type = opts.type;
  }
  return buildQuery(params);
}

function isNotFound(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    (err as { status: number }).status === 404
  );
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminPlacementsApi = {
  /** GET /admin/placement-inventory — the 7-type catalogue. */
  listInventory: async (): Promise<AdminInventoryListResponse> => {
    return adminFetch<AdminInventoryListResponse>('/admin/placement-inventory');
  },

  /**
   * PUT /admin/placement-inventory/:type — edit base price / scarcity cap /
   * lead / advance / tier-restriction / active. `If-Match: <updatedAt>` +
   * mandatory reason. Audited. Gated server-side on `placement_inventory:write`.
   */
  editInventory: async (
    type: string,
    input: EditPlacementInventoryInput,
    ifMatch: string,
  ): Promise<PlacementInventoryConfig> => {
    const res = await adminFetch<DataEnvelope<PlacementInventoryConfig>>(
      `/admin/placement-inventory/${encodeURIComponent(type)}`,
      {
        method: 'PUT',
        headers: { 'If-Match': ifMatch },
        body: input as unknown as Record<string, unknown>,
      },
    );
    return res.data;
  },

  /** GET /admin/placements — paginated cross-venue list (filter status/type/venue). */
  listPlacements: async (opts?: ListPlacementsOpts): Promise<AdminPlacementsListResponse> => {
    return adminFetch<AdminPlacementsListResponse>(
      `/admin/placements${buildPlacementsQuery(opts ?? {})}`,
    );
  },

  /** GET /admin/placements/approvals — the FIFO pending-approval queue. */
  listApprovals: async (opts?: ListApprovalsOpts): Promise<AdminPlacementsListResponse> => {
    return adminFetch<AdminPlacementsListResponse>(
      `/admin/placements/approvals${buildQuery({ page: opts?.page, perPage: opts?.perPage })}`,
    );
  },

  /** GET /admin/placements/:id — detail + creative + audit. */
  placementDetail: async (id: string): Promise<AdminPlacementDetailResponse | null> => {
    try {
      const res = await adminFetch<DataEnvelope<AdminPlacementDetailResponse>>(
        `/admin/placements/${encodeURIComponent(id)}`,
      );
      return res.data;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * POST /admin/placements/:id/approve — → scheduled. Audited. Returns the
   * mapped `Placement` (pages refetch the detail for the joined fields).
   */
  approvePlacement: async (id: string, input: ApprovePlacementInput = {}): Promise<Placement> => {
    const res = await adminFetch<DataEnvelope<Placement>>(
      `/admin/placements/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /**
   * POST /admin/placements/:id/reject — mandatory reason → rejected + refund
   * consumed credit. NO F23 line item. Audited.
   */
  rejectPlacement: async (id: string, input: RejectPlacementInput): Promise<Placement> => {
    const res = await adminFetch<DataEnvelope<Placement>>(
      `/admin/placements/${encodeURIComponent(id)}/reject`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /**
   * POST /admin/placements/:id/cancel — D4 admin override-cancel. Mandatory
   * reason + optional goodwill credit-grant. Audited.
   */
  cancelPlacement: async (id: string, input: CancelPlacementInput): Promise<Placement> => {
    const res = await adminFetch<DataEnvelope<Placement>>(
      `/admin/placements/${encodeURIComponent(id)}/cancel`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /**
   * POST /admin/placements/promote-tournament — the F41 free admin
   * platform-promote of a sponsored tournament (no organizer billing/credit;
   * lands `active`). Mandatory ≥10-char reason. Rate-limited. Gated
   * server-side on `placement_approvals:approve`. Audited.
   */
  promoteTournament: async (input: PromoteTournamentPlacementInput): Promise<Placement> => {
    const res = await adminFetch<DataEnvelope<Placement>>('/admin/placements/promote-tournament', {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return res.data;
  },

  /**
   * POST /admin/venues/:venueId/placement-credits/grant — D4 goodwill grant →
   * positive `placement_credit_adjustments`. Mandatory reason. Rate-limited
   * (`placementCreditGrant`). Gated on `placement_credits:grant`. Audited.
   * Replies 201 `{ data: PlacementCreditAdjustment }` — the inserted
   * adjustment row, not a remaining balance (FIX 6.1 contract test).
   */
  grantCredit: async (
    venueId: string,
    input: GrantPlacementCreditInput,
  ): Promise<PlacementCreditAdjustment> => {
    const res = await adminFetch<DataEnvelope<PlacementCreditAdjustment>>(
      `/admin/venues/${encodeURIComponent(venueId)}/placement-credits/grant`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return res.data;
  },

  /** GET /admin/placements/analytics — platform-wide performance analytics. */
  analytics: async (): Promise<AdminPlacementAnalyticsResponse> => {
    const res = await adminFetch<DataEnvelope<AdminPlacementAnalyticsResponse>>(
      '/admin/placements/analytics',
    );
    return res.data;
  },
};
